import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { PCT } from './format.js';

const BUCKET_COLORS = ['#2563eb', '#0d9488', '#ea580c', '#7c3aed', '#dc2626', '#65a30d', '#0891b2', '#c026d3', '#475569'];

export function toTrendRows(dates: string[], series: Record<string, number[]>): Array<Record<string, number | string>> {
  return dates.map((date, i) => {
    const row: Record<string, number | string> = { date };
    for (const [bucket, values] of Object.entries(series)) {
      row[bucket] = values[i] ?? 0;
    }
    return row;
  });
}

/**
 * A trend is empty when there are no series (no bucket keys) or no dates to
 * plot. The route defaults a missing per-dimension series to `{}`, which would
 * otherwise render a chart with axes but zero <Line>s — a blank graph with no
 * explanation. Render a placeholder instead.
 */
export function isTrendEmpty(dates: string[], series: Record<string, number[]>): boolean {
  if (Object.keys(series).length === 0) return true;
  if (dates.length === 0) return true;
  return false;
}

export interface SliceTrendChartProps {
  dates: string[];
  series: Record<string, number[]>;
  valueFormatter?: (v: number) => string;
  /** Whether values are 0..1 rates (true) or times in seconds (false). Drives Y-axis domain. */
  isRate?: boolean;
}

const TICK_DATE = (d: string) => d.slice(5);

export function SliceTrendChart({ dates, series, valueFormatter = PCT, isRate = true }: SliceTrendChartProps) {
  const data = toTrendRows(dates, series);
  const bucketNames = Object.keys(series);
  const yDomain: [number, number] | ['auto', 'auto'] = isRate ? [0, 1] : ['auto', 'auto'];

  if (isTrendEmpty(dates, series)) {
    return (
      <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>
        No data in this window.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={180}>
      <LineChart data={data} margin={{ top: 6, right: 8, bottom: 18, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
        <XAxis dataKey="date" tickFormatter={TICK_DATE} fontSize={11} stroke="#6b7280" />
        <YAxis tickFormatter={valueFormatter} fontSize={11} stroke="#6b7280" domain={yDomain} />
        <Tooltip formatter={(value: number) => valueFormatter(value)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {bucketNames.map((name, i) => (
          <Line
            key={name}
            type="monotone"
            dataKey={name}
            stroke={BUCKET_COLORS[i % BUCKET_COLORS.length]}
            dot={false}
            strokeWidth={1.5}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
